import { useCallback, useEffect, useState } from 'react';
import { AuthContext, useAuthState } from './hooks/useAuth';
import { useGeolocation } from './hooks/useGeolocation';
import { useNearby } from './hooks/useNearby';
import { useSavedItems } from './hooks/useSavedItems';
import { DiscoveryView } from './views/DiscoveryView';
import { SceneLanding } from './views/SceneLanding';
import { Code, Projects } from 'trace-viewer/cms';
import type { ProjectResponseDto, SceneResponseDto } from 'trace-viewer/cms';
import { apiConfig } from './auth';
import { usePageTitle } from './hooks/usePageTitle';

type View =
    | { type: 'discovery' }
    | { type: 'loading-code'; code: string }
    | { type: 'landing'; scene: SceneResponseDto };

/** Extract a code from the URL path (e.g. "/abc123" → "abc123") */
function getCodeFromPath(): string | null {
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (path === 'map' || path === 'saved') return null; // handled separately
    // Normalise like ?code=: strip dashes/whitespace, since the UI displays
    // codes with a dash ("F63-QA4") and users paste that form into the path.
    return path.replace(/[-\s]/g, '') || null;
}

function getInitialTab(): 'experiences' | 'saved' | 'map' {
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (path === 'map') return 'map';
    if (path === 'saved') return 'saved';
    return 'experiences';
}

/** Parse query parameters from the current URL */
function getQueryParams(): { code?: string; projects?: string[] } {
    const params = new URLSearchParams(window.location.search);
    const result: { code?: string; projects?: string[] } = {};

    const code = params.get('code');
    if (code) {
        // Normalise: strip dashes, whitespace
        result.code = code.replace(/[-\s]/g, '').trim();
    }

    const projects = params.get('projects');
    if (projects) {
        result.projects = projects.split(',').map(s => s.trim()).filter(Boolean);
    }

    return result;
}

export function App() {
    const auth = useAuthState();
    const [initialQuery] = useState(getQueryParams);
    const hasProjectsQuery = (initialQuery.projects?.length ?? 0) > 0;
    const hasCodeQuery = !!initialQuery.code;

    const [view, setView] = useState<View>(() => {
        // ?code= takes priority, then path-based codes
        if (hasCodeQuery) return { type: 'discovery' }; // handled separately once auth ready
        const code = getCodeFromPath();
        if (code) return { type: 'loading-code', code };
        return { type: 'discovery' };
    });
    // Latch: true once discovery has been shown, so geo persists after navigating away
    const [discoveryVisited, setDiscoveryVisited] = useState(() => !getCodeFromPath() || hasCodeQuery);
    // Skip geo-based nearby fetch when ?projects= is present (curated mode)
    const geo = useGeolocation(discoveryVisited && !hasProjectsQuery);
    const nearby = useNearby(auth.token, geo.latitude, geo.longitude);
    const [codeError, setCodeError] = useState<string | null>(null);
    const saved = useSavedItems();
    const [activeTab, setActiveTab] = useState<'experiences' | 'saved' | 'map'>(() => {
        if (hasCodeQuery) return 'saved';
        return getInitialTab();
    });
    const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
    const [expandedScenes, setExpandedScenes] = useState<SceneResponseDto[]>([]);

    // ?projects= curated mode: fetched projects replace nearby data
    const [curatedProjects, setCuratedProjects] = useState<ProjectResponseDto[] | null>(
        hasProjectsQuery ? [] : null, // [] = loading, null = not in curated mode
    );
    const [curatedLoading, setCuratedLoading] = useState(hasProjectsQuery);

    // ?code= loading state: true while the code query is being resolved
    const [codeQueryLoading, setCodeQueryLoading] = useState(hasCodeQuery);
    // Project ID resolved from ?code= that DiscoveryView should auto-select
    const [codeQueryProjectId, setCodeQueryProjectId] = useState<string | null>(null);

    const isDiscoveryVisible = view.type === 'discovery';
    useEffect(() => {
        if (isDiscoveryVisible) setDiscoveryVisited(true);
    }, [isDiscoveryVisible]);

    // ?code= query: resolve and save to Saved tab once auth is ready
    useEffect(() => {
        if (!hasCodeQuery || !auth.token) return;
        const code = initialQuery.code!;
        let cancelled = false;
        (async () => {
            try {
                const res = await new Code(apiConfig(auth.token!)).codeDetail(code);
                if (cancelled) return;
                const result = res.data;

                if (result.isScene && result.scene) {
                    // Scene code → save scene and go to landing
                    saved.saveScene(result.scene);
                    setCodeQueryLoading(false);
                    setView({ type: 'landing', scene: result.scene });
                } else if (result.isProject && result.project) {
                    let scenes = result.project.scenes ?? [];
                    if (scenes.length === 0 && result.project.id) {
                        const scenesRes = await new Projects(apiConfig(auth.token!)).scenesList(result.project.id);
                        if (cancelled) return;
                        scenes = scenesRes.data ?? [];
                    }
                    saved.saveProject(result.project, scenes);
                    setCodeQueryProjectId(result.project.id!);
                    setCodeQueryLoading(false);
                } else {
                    setCodeQueryLoading(false);
                    setCodeError('Code not found');
                }
            } catch {
                if (!cancelled) {
                    setCodeQueryLoading(false);
                    setCodeError(`Could not find "${code}"`);
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCodeQuery, auth.token]);

    // ?projects= query: fetch each project by ID/shortcode once auth is ready
    useEffect(() => {
        if (!hasProjectsQuery || !auth.token) return;
        const codes = initialQuery.projects!;
        let cancelled = false;
        (async () => {
            const results: ProjectResponseDto[] = [];
            const api = new Projects(apiConfig(auth.token!));
            await Promise.all(codes.map(async (code) => {
                try {
                    const res = await api.projectsDetail(code);
                    if (res.data) results.push(res.data);
                } catch {
                    // Skip projects that fail to resolve
                }
            }));
            if (cancelled) return;
            // Preserve the order from the query string
            const orderMap = new Map(codes.map((c, i) => [c.toLowerCase(), i]));
            results.sort((a, b) => {
                const aKey = a.shortCode?.toLowerCase() ?? a.id?.toLowerCase() ?? '';
                const bKey = b.shortCode?.toLowerCase() ?? b.id?.toLowerCase() ?? '';
                return (orderMap.get(aKey) ?? orderMap.get(a.id?.toLowerCase() ?? '') ?? 999)
                    - (orderMap.get(bKey) ?? orderMap.get(b.id?.toLowerCase() ?? '') ?? 999);
            });
            setCuratedProjects(results);
            setCuratedLoading(false);
            // Auto-expand first project
            if (results.length > 0) {
                const first = results[0];
                const scenes = first.scenes ?? [];
                setExpandedProjectId(first.id!);
                setExpandedScenes(scenes);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasProjectsQuery, auth.token]);

    // Derive the page title from current view state
    const pageTitle = (() => {
        if (view.type === 'landing') return view.scene.name ?? undefined;
        if (isDiscoveryVisible && activeTab === 'map') return 'Map';
        if (isDiscoveryVisible && activeTab === 'saved') return 'Saved';
        return undefined; // bare "Trace Viewer"
    })();
    usePageTitle(pageTitle);

    // Resolve a code to a scene via the API
    const resolveCode = useCallback(async (code: string, token: string): Promise<SceneResponseDto | null> => {
        try {
            const res = await new Code(apiConfig(token)).codeDetail(code);
            const result = res.data;

            if (result.isScene && result.scene) {
                return result.scene;
            } else if (result.isProject && result.project?.scenes?.length) {
                return result.project.scenes[0];
            } else if (result.isProject && result.project) {
                const scenesRes = await new Projects(apiConfig(token)).scenesList(result.project.id!);
                const scenes = scenesRes.data ?? [];
                return scenes.length > 0 ? scenes[0] : null;
            }
        } catch {
            // Fall through
        }
        return null;
    }, []);

    // When auth is ready and we have a code to resolve, look it up
    useEffect(() => {
        if (view.type !== 'loading-code' || !auth.token) return;

        let cancelled = false;
        (async () => {
            const scene = await resolveCode(view.code, auth.token!);
            if (cancelled) return;
            if (scene) {
                setView({ type: 'landing', scene });
            } else {
                setCodeError(`Could not find "${view.code}"`);
                setView({ type: 'discovery' });
                window.history.replaceState(null, '', '/');
            }
        })();
        return () => { cancelled = true; };
    }, [view.type, auth.token, resolveCode]);

    // Handle browser back/forward
    useEffect(() => {
        const onPopState = () => {
            const code = getCodeFromPath();
            if (code) {
                if (auth.token) {
                    setView({ type: 'loading-code', code });
                }
            } else {
                setView({ type: 'discovery' });
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [auth.token]);

    const navigateToLanding = (scene: SceneResponseDto) => {
        const code = scene.shortCode;
        if (code) {
            window.history.pushState(null, '', `/${code}`);
        }
        setView({ type: 'landing', scene });
    };

    const handleExpandedChange = useCallback((id: string | null, scenes: SceneResponseDto[]) => {
        setExpandedProjectId(id);
        setExpandedScenes(scenes);
    }, []);

    const navigateToDiscovery = () => {
        const url = activeTab === 'map' ? '/map' : activeTab === 'saved' ? '/saved' : '/';
        window.history.pushState(null, '', url);
        setCodeError(null);
        setView({ type: 'discovery' });
    };

    if (auth.error) {
        return (
            <div className="app-loading">
                <span className="error-text">{auth.error}</span>
                <button className="retry-button" onClick={auth.retry}>Retry</button>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={auth}>
            {isDiscoveryVisible && (
                <DiscoveryView
                    onSelectScene={navigateToLanding}
                    initialCodeError={codeError}
                    tab={activeTab}
                    onTabChange={setActiveTab}
                    geo={geo}
                    nearby={nearby}
                    expandedProjectId={expandedProjectId}
                    expandedScenes={expandedScenes}
                    onExpandedChange={handleExpandedChange}
                    savedProjects={saved.savedProjects}
                    savedScenes={saved.savedScenes}
                    onSaveProject={saved.saveProject}
                    onRemoveProject={saved.removeProject}
                    onRemoveScene={saved.removeScene}
                    curatedProjects={curatedProjects}
                    curatedLoading={curatedLoading}
                    codeQueryLoading={codeQueryLoading}
                    codeQueryProjectId={codeQueryProjectId}
                />
            )}
            {view.type === 'loading-code' && (
                <div className="scene-viewer">
                    <div className="landing">
                        <button className="landing-back" onClick={navigateToDiscovery} aria-label="Back to experiences">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7"/>
                            </svg>
                        </button>
                        <div className="landing-image-wrap placeholder">
                            <div className="landing-image-skeleton" />
                        </div>
                        <div className="landing-footer">
                            <div className="landing-title-skeleton" />
                            <button className="landing-play" disabled>
                                <span>Loading...</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {view.type === 'landing' && (
                <SceneLanding
                    scene={view.scene}
                    onBack={navigateToDiscovery}
                />
            )}
        </AuthContext.Provider>
    );
}
